/**
 * Service Bus sources: queries, keys, and mutations for sources, the inbound
 * log, waiting messages, and dead letters.
 *
 * Peeks and dead-letter listings read Service Bus live, so they aren't
 * cached for long and never refetch on focus.
 */
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { api, unwrap } from '@/api/client'
import { useInfiniteList } from '@/api/pagination'
import type {
  CreateServiceBusSource,
  InboundOutcome,
  SbPreviewRequest,
  UpdateServiceBusSource,
} from '@/api/types'
import type { components } from '@/api/schema'

type S = components['schemas']

export const sbKeys = {
  all: ['service-bus'] as const,
  list: (appId: string) => ['service-bus', 'list', appId] as const,
  detail: (id: string) => ['service-bus', 'detail', id] as const,
  inbound: (p: { source?: string; app?: string; outcome?: InboundOutcome; q?: string }) =>
    ['service-bus', 'inbound', p] as const,
  inboundEntry: (id: string) => ['service-bus', 'inbound-entry', id] as const,
  peek: (id: string, from?: number) => ['service-bus', 'detail', id, 'peek', { from }] as const,
  deadLetters: (id: string, from?: number) =>
    ['service-bus', 'detail', id, 'dead-letters', { from }] as const,
  context: (id: string, samples: number) =>
    ['service-bus', 'detail', id, 'context', { samples }] as const,
}

export const sourcesQuery = (appId: string) =>
  queryOptions({
    queryKey: sbKeys.list(appId),
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/applications/{id}/service-bus-sources', {
          params: { path: { id: appId } },
        }),
      ),
    // The worker updates state and last received in the background.
    refetchInterval: 15_000,
  })

export const sourceQuery = (id: string) =>
  queryOptions({
    queryKey: sbKeys.detail(id),
    queryFn: () =>
      unwrap(api.GET('/admin/v1/service-bus-sources/{id}', { params: { path: { id } } })),
    refetchInterval: 10_000,
  })

export function useCreateSource(appId: string) {
  return useMutation({
    mutationFn: (body: CreateServiceBusSource) =>
      unwrap(
        api.POST('/admin/v1/applications/{id}/service-bus-sources', {
          params: { path: { id: appId } },
          body,
        }),
      ),
    meta: { invalidates: [sbKeys.all], successMessage: 'Source added', errorToast: false },
  })
}

export function useUpdateSource(options: { successMessage?: string; inlineErrors?: boolean } = {}) {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateServiceBusSource }) =>
      unwrap(api.PATCH('/admin/v1/service-bus-sources/{id}', { params: { path: { id } }, body })),
    meta: {
      invalidates: [sbKeys.all],
      successMessage: options.successMessage,
      errorToast: !options.inlineErrors,
    },
  })
}

export function useDeleteSource() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/admin/v1/service-bus-sources/{id}', { params: { path: { id } } })),
    meta: { invalidates: [sbKeys.all], successMessage: 'Source deleted' },
  })
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/admin/v1/service-bus-sources/{id}/test-connection', {
          params: { path: { id } },
        }),
      ),
    // The result, failure included, is shown inline.
    meta: { errorToast: false },
  })
}

/** Waiting messages, peeked (nothing is removed). Fetched on demand. */
export function usePeek(id: string, from: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: sbKeys.peek(id, from),
    queryFn: () =>
      unwrap(
        api.POST('/admin/v1/service-bus-sources/{id}/peek', {
          params: { path: { id } },
          body: { limit: 25, from_sequence: from ?? null },
        }),
      ),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useDeadLetters(id: string, from: number | undefined) {
  return useQuery({
    queryKey: sbKeys.deadLetters(id, from),
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/service-bus-sources/{id}/dead-letters', {
          params: { path: { id }, query: { from_sequence: from, limit: 50 } },
        }),
      ),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useDeadLetterAction(action: 'retry' | 'delete') {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: S['DeadLetterActionRequest'] }) =>
      action === 'retry'
        ? unwrap(
            api.POST('/admin/v1/service-bus-sources/{id}/dead-letters/retry', {
              params: { path: { id } },
              body,
            }),
          )
        : unwrap(
            api.POST('/admin/v1/service-bus-sources/{id}/dead-letters/delete', {
              params: { path: { id } },
              body,
            }),
          ),
    meta: { invalidates: [sbKeys.all] },
  })
}

export function usePreview(id: string) {
  return useMutation({
    mutationFn: (body: SbPreviewRequest) =>
      unwrap(
        api.POST('/admin/v1/service-bus-sources/{id}/preview', {
          params: { path: { id } },
          body,
        }),
      ),
    meta: { errorToast: false },
  })
}

/** Completion for mapping expressions, from sample bodies or the inbound log. */
export function useMappingContext(
  id: string,
  samples: { bodies: unknown[]; properties: unknown[] } | undefined,
) {
  return useQuery({
    queryKey: [...sbKeys.context(id, samples?.bodies.length ?? -1), samples],
    queryFn: () =>
      unwrap(
        api.POST('/admin/v1/service-bus-sources/{id}/context', {
          params: { path: { id } },
          body: samples
            ? {
                bodies: samples.bodies as Record<string, never>[],
                properties: samples.properties as Record<string, never>[],
              }
            : {},
        }),
      ),
    staleTime: 60_000,
  })
}

export interface InboundFilters {
  source?: string
  app?: string
  outcome?: InboundOutcome
  q?: string
}

export function useInboundLog(f: InboundFilters) {
  return useInfiniteList({
    queryKey: sbKeys.inbound(f),
    fetchPage: (cursor) =>
      unwrap(
        api.GET('/admin/v1/inbound-messages', {
          params: {
            query: {
              source: f.source,
              app: f.app,
              outcome: f.outcome,
              q: f.q || undefined,
              cursor,
              limit: 50,
            },
          },
        }),
      ),
    refetchInterval: 15_000,
  })
}

export const inboundEntryQuery = (id: string) =>
  queryOptions({
    queryKey: sbKeys.inboundEntry(id),
    queryFn: () =>
      unwrap(api.GET('/admin/v1/inbound-messages/{id}', { params: { path: { id } } })),
  })

export function useReprocess() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/admin/v1/inbound-messages/{id}/reprocess', { params: { path: { id } } }),
      ),
    meta: { invalidates: [sbKeys.all], errorToast: false },
  })
}
